// React
import { memo, useCallback } from "react";

// Helpers
import { classNames } from "helpers";

// Tipovi i konstante
import { KEY_GROUPS } from "./consts";
import { PricingKeyPickerProps } from "./types";

/**
 * Odabir ključa karticama.
 *
 * Padajući popis je nudio gole šifre, pa se `vozilo.core+najam+prodaja` moralo čitati slovo po
 * slovo. Kartica nosi naziv, vrijednost koja danas vrijedi i tek onda šifru, a skupine odvajaju
 * pretplatu od mjerenih stavaka.
 */
const PricingKeyPicker: React.FC<PricingKeyPickerProps> = (props: PricingKeyPickerProps) => {
  const { options, value, error, onChange } = props;

  const handleSelect = useCallback(
    (code: string) => () => {
      onChange(code);
    },
    [onChange]
  );

  return (
    <div>
      <p className="text-sm font-medium text-slate-700 mb-3">
        Ključ <span className="text-red-500">*</span>
      </p>

      <div className="flex flex-col gap-6">
        {KEY_GROUPS.map((section) => {
          const items = options.filter((option) => option.group === section.group);

          if (items.length === 0) return null;

          return (
            <div key={section.group}>
              <div className="mb-2">
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                  {section.title}
                </h3>

                <p className="text-xs text-slate-400">{section.hint}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {items.map((option) => {
                  const isSelected = option.code === value;

                  return (
                    <button
                      key={option.code}
                      type="button"
                      onClick={handleSelect(option.code)}
                      aria-pressed={isSelected}
                      className={classNames(
                        "text-left border px-4 py-3 transition-colors",
                        isSelected
                          ? "border-sky-600 bg-sky-50 ring-1 ring-sky-600"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p
                          className={classNames(
                            "text-sm font-semibold",
                            isSelected ? "text-sky-900" : "text-slate-700"
                          )}
                        >
                          {option.name}
                        </p>

                        <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">
                          {option.value}
                        </span>
                      </div>

                      {option.description && (
                        <p className="text-xs text-slate-500 mt-1">{option.description}</p>
                      )}

                      {/* Šifra ide na kraj: po njoj se prepoznaje, ali ne treba se čitati prva. */}
                      <p className="text-xs text-slate-400 font-mono mt-1">{option.code}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
    </div>
  );
};

export default memo(PricingKeyPicker);
